import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  type CreateTopupResponse,
  TOPUP_MAX_CNY_CENTS,
  TOPUP_MIN_CNY_CENTS,
  type TopupPayType,
} from '@nadoutong/contracts';
import { PlatformApiError } from '../api/client.ts';
import {
  BALANCE_QUERY_KEY,
  TRANSACTIONS_PREFIX,
  useCreateTopup,
  useTopupQuote,
  useTopupStatus,
} from '../api/useBilling.ts';
import { Modal } from '../components/Modal.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { formatUsdCents } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { INPUT_CLASS } from '../styles/forms.ts';

/**
 * `/billing/topup` — tenant-facing topup form (Epic #84 W4 P9, refactored
 * P17 for CNY-integer-first semantics).
 *
 * Flow:
 *   1. User picks an amount (CNY whole yuan, ¥1–¥1500) + payment method.
 *   2. On submit we POST `/v1/billing/topup` with `{ amountCnyCents,
 *      payType }` and branch on the shape of the response:
 *        - `qrCodeUrl` / `imageUrl` present (PRIMARY PATH after P18) →
 *          open a modal that shows the QR + polls
 *          `/v1/billing/topup/:id` every 3s until the status
 *          transitions out of `pending`. Tenant scans with their
 *          phone's WeChat app and stays inside console-web.
 *        - `payUrl` present (fallback) → desktop hosted cashier,
 *          redirect via `window.location.href`. Only hit when the
 *          provider couldn't produce a QR.
 *
 * Why CNY-integer-first (not USD):
 *   - The payment aggregator (ZPay) operates in CNY; merchant dashboard
 *     reconciliation is in whole yuan. Sub-yuan amounts like ¥7.89 are
 *     accepted at the cashier but produce non-integer line items that
 *     break downstream accounting.
 *   - USD balance is still the account unit of account (ledger lives in
 *     USD cents). We derive `amountUsdCents = floor(amountCnyCents / R)`
 *     server-side, trading <1 cent of customer credit for exact whole-
 *     yuan CNY — the ledger bias favours platform margin.
 *
 * Design notes:
 *   - USD display on this form is an *estimate* from the server-side
 *     topup quote endpoint. The authoritative rate still locks at order
 *     creation and is echoed on the order response/status.
 *   - Validation is local-only clamping; the server re-validates
 *     (whole-yuan + 100 ≤ cents ≤ 150_000) and returns
 *     `TOPUP_AMOUNT_TOO_SMALL` / `TOPUP_AMOUNT_TOO_LARGE` /
 *     `TOPUP_PROVIDER_ERROR`. Any error code is surfaced via toast.
 *   - Transaction history on the main /billing page stays USD-only; CNY
 *     only appears here (topup form) and on the receipt (return page).
 */

const DEFAULT_CNY_CENTS = 1_000; // ¥10
const DEFAULT_CNY_YUAN_TEXT = '10';
const PAY_TYPE_OPTIONS: readonly { value: TopupPayType; labelKey: I18nKey }[] = [
  { value: 'wxpay', labelKey: 'topup.pay_type.wxpay' },
];

/**
 * Ceiling on the QR modal's polling window. After this many ms with no
 * terminal status we stop polling and show a "taking longer than usual"
 * inline message alongside the QR (we keep the QR visible — the user
 * might still pay via it). Matches the `PENDING_CEILING_MS` on the
 * return page; both exist so an unattended tab stops burning
 * `GET /v1/billing/topup/:id` requests forever.
 */
export const QR_POLL_CEILING_MS = 60_000;

export function BillingTopupPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const createM = useCreateTopup();
  const t = useT();

  const [amountCnyCents, setAmountCnyCents] = useState<number>(DEFAULT_CNY_CENTS);
  const [amountText, setAmountText] = useState<string>(DEFAULT_CNY_YUAN_TEXT);
  const [payType, setPayType] = useState<TopupPayType>('wxpay');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [qrOrder, setQrOrder] = useState<CreateTopupResponse | null>(null);

  const quoteQ = useTopupQuote(amountCnyCents, {
    enabled:
      amountCnyCents > 0 &&
      amountCnyCents % 100 === 0 &&
      amountCnyCents >= TOPUP_MIN_CNY_CENTS &&
      amountCnyCents <= TOPUP_MAX_CNY_CENTS,
  });

  function handleAmountChange(raw: string): void {
    setAmountText(raw);
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      setAmountCnyCents(0);
      setValidationError(null);
      return;
    }
    // Only accept a positive integer — whole yuan. A dot, comma, or any
    // non-digit character is a fast reject before any business bound
    // check. Lets the server continue to be the final authority on the
    // range and whole-yuan rules (we match them eagerly for UX).
    if (!/^\d+$/.test(trimmed)) {
      setValidationError(t('topup.error.whole_yuan'));
      return;
    }
    const yuan = Number(trimmed);
    if (!Number.isFinite(yuan) || yuan <= 0) {
      setValidationError(t('topup.error.positive'));
      return;
    }
    setAmountCnyCents(yuan * 100);
    setValidationError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (createM.isPending) {
      return;
    }
    if (amountCnyCents <= 0) {
      setValidationError(t('topup.error.required'));
      return;
    }
    if (amountCnyCents % 100 !== 0) {
      // Defensive — the input validator should have caught this already.
      setValidationError(t('topup.error.no_cents'));
      return;
    }
    if (amountCnyCents < TOPUP_MIN_CNY_CENTS) {
      setValidationError(t('topup.error.min', { amount: formatCnyCents(TOPUP_MIN_CNY_CENTS) }));
      return;
    }
    if (amountCnyCents > TOPUP_MAX_CNY_CENTS) {
      setValidationError(t('topup.error.max', { amount: formatCnyCents(TOPUP_MAX_CNY_CENTS) }));
      return;
    }
    setValidationError(null);
    try {
      const order = await createM.mutateAsync({
        amountCnyCents,
        payType,
      });
      // Response priority matches the provider (see
      // `packages/payments/src/zpay/provider.ts`): scan-to-pay QR is the
      // primary UX, and the tenant stays inside console-web to scan it
      // with their phone's WeChat app. `payUrl` is only
      // surfaced when the provider couldn't produce a QR — in that case
      // we fall back to a browser redirect so the tenant isn't blocked.
      if (
        (typeof order.qrCodeUrl === 'string' && order.qrCodeUrl.length > 0) ||
        (typeof order.imageUrl === 'string' && order.imageUrl.length > 0)
      ) {
        setQrOrder(order);
        return;
      }
      if (typeof order.payUrl === 'string' && order.payUrl.length > 0) {
        window.location.href = order.payUrl;
        return;
      }
      // Backend returned neither — surface a toast; this should never
      // happen given the contract, but fail soft.
      toast.error(t('topup.error.no_redirect'));
    } catch (err) {
      const message =
        err instanceof PlatformApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      toast.error(message);
    }
  }

  function handleModalClose(): void {
    setQrOrder(null);
    // User may have paid in the ZPay app already; nudge a refresh so
    // the /billing view catches up without a manual reload.
    void qc.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: TRANSACTIONS_PREFIX });
  }

  return (
    <section data-testid="billing-topup-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('topup.title')}
        </h2>
        <p className="text-sm text-outline">{t('topup.subtitle')}</p>
      </header>

      <article
        data-testid="billing-topup-card"
        className="flex flex-col gap-6 rounded-xl bg-surface-container p-6"
      >
        <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('topup.amount.label')}
            </span>
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="font-mono text-sm text-on-surface-variant"
                data-testid="billing-topup-amount-prefix"
              >
                ¥
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                value={amountText}
                onChange={(e) => handleAmountChange(e.currentTarget.value)}
                disabled={createM.isPending}
                placeholder="10"
                data-testid="billing-topup-amount"
                className={INPUT_CLASS}
                aria-describedby="billing-topup-amount-hint"
              />
            </div>
            <span
              id="billing-topup-amount-hint"
              className="text-[10px] uppercase tracking-[0.1em] text-outline"
            >
              {t('topup.amount.hint', {
                min: formatCnyCents(TOPUP_MIN_CNY_CENTS),
                max: formatCnyCents(TOPUP_MAX_CNY_CENTS),
              })}
            </span>
          </label>

          <div className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('topup.usd.label')}
            </span>
            <div
              className="rounded-lg bg-surface-container-lowest px-3 py-2 font-mono text-lg text-primary"
              data-testid="billing-topup-usd-display"
            >
              {quoteQ.isSuccess ? `≈ ${formatUsdCents(quoteQ.data.amountUsdCents)}` : '—'}
            </div>
            <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
              {quoteQ.isSuccess
                ? t('topup.usd.hint', { rate: quoteQ.data.appliedRate.toFixed(4) })
                : quoteQ.isError
                  ? t('topup.usd.hint_unavailable')
                  : t('topup.usd.hint_loading')}
            </span>
          </div>

          <fieldset className="flex flex-col gap-3">
            <legend className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('topup.pay_type.legend')}
            </legend>
            <div className="flex flex-col gap-2">
              {PAY_TYPE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-3 rounded-lg bg-surface-container-low px-3 py-2 text-sm text-on-surface"
                >
                  <input
                    type="radio"
                    name="pay_type"
                    value={opt.value}
                    checked={payType === opt.value}
                    onChange={() => setPayType(opt.value)}
                    disabled={createM.isPending}
                    data-testid={`billing-topup-pay-type-${opt.value}`}
                    className="accent-primary"
                  />
                  <span>{t(opt.labelKey)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {validationError !== null ? (
            <p
              className="text-xs font-medium text-error"
              role="alert"
              data-testid="billing-topup-error"
            >
              {validationError}
            </p>
          ) : null}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <Link
              to="/billing"
              data-testid="billing-topup-cancel"
              className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
            >
              {t('topup.cancel')}
            </Link>
            <button
              type="submit"
              disabled={createM.isPending || amountCnyCents <= 0}
              data-testid="billing-topup-submit"
              className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-primary-glow disabled:opacity-40 disabled:pointer-events-none"
            >
              {createM.isPending ? t('topup.submitting') : t('topup.submit')}
            </button>
          </div>

          <p className="text-[11px] text-outline">{t('topup.fee_hint')}</p>
        </form>
      </article>

      {qrOrder !== null ? (
        <TopupQrModal
          order={qrOrder}
          onClose={handleModalClose}
          onPaid={() => {
            // Balance + tx queries already refreshed by the return-page
            // flow, but nudge here too so the tenant sees the updated
            // numbers after closing the modal.
            void qc.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
            void qc.invalidateQueries({ queryKey: TRANSACTIONS_PREFIX });
            navigate(`/billing/topup/return?order_id=${encodeURIComponent(qrOrder.orderId)}`);
          }}
        />
      ) : null}
    </section>
  );
}

interface TopupQrModalProps {
  readonly order: CreateTopupResponse;
  readonly onClose: () => void;
  readonly onPaid: () => void;
}

function TopupQrModal({ order, onClose, onPaid }: TopupQrModalProps): JSX.Element {
  const t = useT();
  const [pollEnabled, setPollEnabled] = useState(true);
  const statusQ = useTopupStatus(order.orderId, { enabled: pollEnabled });
  const status = statusQ.data?.status ?? 'pending';
  const paidFiredRef = useRef(false);

  // Ceiling on the polling window: after `QR_POLL_CEILING_MS` with no
  // terminal status, stop polling and surface a "taking longer than
  // usual" inline hint next to the QR. We keep the QR visible because
  // the user might still be completing payment in their phone app; we
  // just refuse to burn requests while they do.
  useEffect(() => {
    const handle = setTimeout(() => {
      setPollEnabled(false);
    }, QR_POLL_CEILING_MS);
    return () => clearTimeout(handle);
  }, []);

  const slowMode = !pollEnabled && status === 'pending';

  // Fire `onPaid` exactly once when the status transitions to paid.
  // `useTopupStatus` already stops polling on any non-pending value.
  useEffect(() => {
    if (status === 'paid' && !paidFiredRef.current) {
      paidFiredRef.current = true;
      onPaid();
    }
  }, [status, onPaid]);

  const qrTarget =
    typeof order.imageUrl === 'string' && order.imageUrl.length > 0 ? order.imageUrl : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="billing-topup-qr-title"
      testId="billing-topup-qr-modal"
    >
      <div className="flex flex-col gap-4 p-6">
        <header className="flex flex-col gap-1">
          <h3
            id="billing-topup-qr-title"
            className="text-base font-semibold tracking-tight text-on-surface"
          >
            {t('topup.qr.title')}
          </h3>
          <p className="text-xs text-outline">
            {t('topup.qr.order_prefix')}
            <span className="font-mono text-on-surface-variant">{order.orderId}</span>
          </p>
        </header>

        <div className="flex flex-col items-center gap-4 rounded-lg bg-surface-container-lowest p-6">
          {qrTarget !== null ? (
            <img
              src={qrTarget}
              alt={t('topup.qr.image_alt')}
              className="h-56 w-56 rounded-lg bg-on-surface p-2"
              data-testid="billing-topup-qr-image"
            />
          ) : (
            <div
              className="flex h-56 w-56 flex-col items-center justify-center gap-2 rounded-lg bg-surface-container text-center"
              data-testid="billing-topup-qr-fallback"
            >
              <span
                aria-hidden="true"
                className="material-symbols-outlined text-[48px] text-primary"
              >
                qr_code_2
              </span>
              <p className="px-3 text-[11px] leading-snug text-outline">
                {t('topup.qr.fallback_hint')}
              </p>
            </div>
          )}
          {order.qrCodeUrl !== undefined && order.qrCodeUrl.length > 0 ? (
            <p
              className="break-all font-mono text-[11px] text-outline"
              data-testid="billing-topup-qr-url"
            >
              {order.qrCodeUrl}
            </p>
          ) : null}
        </div>

        <div
          className="flex items-center gap-2 rounded-lg bg-surface-container-low px-3 py-2"
          data-testid="billing-topup-qr-status"
          role="status"
        >
          <span
            aria-hidden="true"
            className={`h-2 w-2 rounded-full ${status === 'pending' && pollEnabled ? 'bg-tertiary animate-pulse-tertiary' : 'bg-primary'}`}
          />
          <span className="text-xs text-on-surface">
            {status === 'paid'
              ? t('topup.qr.status.paid')
              : status !== 'pending'
                ? t('topup.qr.status.other', { status })
                : pollEnabled
                  ? t('topup.qr.status.pending')
                  : t('topup.qr.status.slow')}
          </span>
        </div>

        {slowMode ? (
          <p
            className="rounded-lg bg-surface-container-low px-3 py-2 text-[11px] leading-snug text-outline"
            data-testid="billing-topup-qr-slow"
          >
            {t('topup.qr.slow_notice')}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="billing-topup-qr-close"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('topup.qr.close')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Format CNY cents as `¥ 12.34`. Mirrors `formatUsdCents` in `formatters.ts`. */
function formatCnyCents(cents: number): string {
  const magnitude = Math.abs(cents) / 100;
  return `¥ ${magnitude.toFixed(2)}`;
}
